import { App, Form } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomModal, CustomTypography } from "sipli-fleet-core/components";
import ModalStepHeader from "src/components/ModalStepHeader/ModalStepHeader";
import {
  useMutationCreateWarehouseUnit,
  useQueryGetWarehouseUnitOperationTypeLookup,
} from "src/queries/warehouseUnits.queries";
import type { WarehouseUnitType } from "src/services/warehouseUnitService";
import { useSelectDistributor } from "src/store/selectDistributorStore";
import { getApiErrorMessage } from "src/util/apiError";
import PositionFields from "./PositionFields";
import UnitInfoFields from "./UnitInfoFields";
import {
  getEntityCode,
  getEntityName,
  getOptionalDistributorId,
  normalizeVariantKey,
  useIsFuelSensor,
  useUnitTypeLookup,
  type UnitFormValues,
} from "./unitFormShared";

interface AddUnitModalProps {
  open: boolean;
  defaultUnitType?: WarehouseUnitType;
  onClose: () => void;
  onCreated?: () => void;
}

const UNIT_INFO_BASE_FIELDS: (keyof UnitFormValues)[] = ["unitTypeId", "modelId", "serial"];
const DEVICE_ONLY_FIELDS: (keyof UnitFormValues)[] = ["imei"];
const POSITION_STEP_FIELDS: (keyof UnitFormValues)[] = [
  "warehouseId",
  "areaId",
  "laneId",
  "shelfId",
  "positionId",
];

const AddUnitModal = ({ open, defaultUnitType, onClose, onCreated }: AddUnitModalProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<UnitFormValues>();
  const { selectedDistributor } = useSelectDistributor();
  const distributorId = getOptionalDistributorId(selectedDistributor);
  const [step, setStep] = useState(0);

  const { items: unitTypeItems } = useUnitTypeLookup(open);
  const { data: operationTypeData } = useQueryGetWarehouseUnitOperationTypeLookup({ enabled: open });
  const operationTypeItems = useMemo(
    () => operationTypeData?.data?.response ?? [],
    [operationTypeData]
  );
  const isFuelSensor = useIsFuelSensor(form, open);
  const { mutateAsync: createUnit, isPending: isCreating } = useMutationCreateWarehouseUnit();

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setStep(0);
      return;
    }

    if (form.getFieldValue("unitTypeId") === undefined && unitTypeItems.length > 0) {
      const defaultEntry = unitTypeItems.find(
        (item) => getEntityCode(item) === (defaultUnitType ?? "DVC")
      );
      if (defaultEntry?.id !== undefined) {
        form.setFieldValue("unitTypeId", defaultEntry.id);
      }
    }
  }, [open, defaultUnitType, unitTypeItems, form]);

  useEffect(() => {
    if (!open) return;
    if (form.getFieldValue("operationTypeId") !== undefined) return;

    const receivedEntry = operationTypeItems.find(
      (item) => normalizeVariantKey(getEntityName(item)) === "received"
    );
    if (receivedEntry?.id !== undefined) {
      form.setFieldValue("operationTypeId", receivedEntry.id);
    }
  }, [open, operationTypeItems, form]);

  const handleClose = () => {
    form.resetFields();
    setStep(0);
    onClose();
  };

  const handleNext = async () => {
    const fields = isFuelSensor
      ? UNIT_INFO_BASE_FIELDS
      : [...UNIT_INFO_BASE_FIELDS, ...DEVICE_ONLY_FIELDS];

    try {
      await form.validateFields(fields);
      setStep(1);
    } catch {
      // validation errors are already shown on the fields
    }
  };

  const handleStepClick = (targetStep: number) => {
    if (targetStep < step) {
      setStep(targetStep);
      return;
    }

    if (targetStep === 1) {
      void handleNext();
    }
  };

  const handleSave = async () => {
    try {
      await form.validateFields(POSITION_STEP_FIELDS);
      const values = form.getFieldsValue(true);

      const payload = {
        modelId: values.modelId as string | number,
        unitTypeId: values.unitTypeId as string | number,
        warehouseId: values.warehouseId as string | number,
        positionId: values.positionId,
        operationTypeId: values.operationTypeId as string | number,
        quantity: 1,
        ...(distributorId ? { idDistributor: distributorId } : {}),
        serial: values.serial,
        unitStatusId: values.unitStatusId,
        ...(isFuelSensor
          ? { length: values.length }
          : {
              imei: values.imei,
              phoneNumber: values.phoneNumber,
              active: values.active === "true",
            }),
      };

      await createUnit(payload);
      message.success(t("unit_added_successfully"));
      onCreated?.();
      handleClose();
    } catch (error) {
      if (error instanceof Error) {
        message.error(getApiErrorMessage(error) ?? t("failed_to_add_unit"));
      }
    }
  };

  return (
    <CustomModal
      open={open}
      onCancel={handleClose}
      okText={step === 0 ? t("next") : t("save")}
      cancelText={t("cancel")}
      onOk={step === 0 ? handleNext : handleSave}
      okBtnLoading={isCreating}
      width={835}
      destroyOnHidden
      closable={false}
    >
      <ModalStepHeader
        steps={[t("unit_info"), t("position")]}
        current={step}
        onStepClick={handleStepClick}
        onClose={handleClose}
        closeLabel={t("cancel")}
      />

      <CustomTypography.CustomTitle className="mb-8! text-xl! font-bold!">
        {t("add_unit")}
      </CustomTypography.CustomTitle>

      <div className="mb-3.5 flex flex-col gap-2">
        <CustomTypography.CustomText className="block text-lg! leading-5.5! font-bold! text-black!">
          {step === 0 ? t("unit_info") : t("position")}
        </CustomTypography.CustomText>
      </div>

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ active: "true" }}
        className="all-units-form"
      >
        {step === 0 ? (
          <UnitInfoFields form={form} open={open} />
        ) : (
          <PositionFields form={form} open={open} />
        )}
      </Form>
    </CustomModal>
  );
};

export default AddUnitModal;
